// React imports
import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';

// MUI imports
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import Button from '@mui/material/Button';
import { blue } from '@mui/material/colors';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
// Font awesome icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCaretLeft } from '@fortawesome/free-solid-svg-icons';

export default function Header(props) {
    const { icon, title, subtitle, subtitleProps, tabs, buttons, noMarginBottom, onBack, fixed, width } = props;
    const theme = useTheme();
    // Below the md breakpoint the left menu is hidden and opens from this button instead. The
    // organization layout provides it; every page header shows it, so the menu is reachable on
    // a phone from any page. It hides itself on wider screens.
    const DrawerIcon = useOutletContext()?.DrawerIcon || null;

    return (
        <>
            <Box sx={{ display: 'flex', height: '64px', background: theme.palette.panel1, alignItems: 'center', borderBottom: `1px solid ${theme.palette.border1}`, marginBottom: noMarginBottom ? '0px' : '12px', ...(fixed ? { position: 'fixed', top: '0px', width: { xs: '100%', md: width || 'calc(100% - 273px)' }, zIndex: 1 }  : {}) }}>
                {DrawerIcon && (
                    <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', height: '64px', pl: '12px', borderRight: `1px solid ${theme.palette.border1}` }}>
                        {DrawerIcon}
                    </Box>
                )}
                {onBack && (
                    <Box sx={{ flex: 0, display: 'flex', height: '64px', borderRight: `1px solid ${theme.palette.border1}`, alignItems: 'center', padding: '0px 12px' }} >
                        <Button color='inherit' sx={{ minWidth: '0px' }} onClick={onBack}>
                            <FontAwesomeIcon icon={faCaretLeft} />
                        </Button>
                    </Box>
                )}
                <Box sx={{ flex: 0, display: 'flex', height: '64px', background: theme.palette.panel1, borderRight: `1px solid ${theme.palette.border1}`, borderBottom: `1px solid ${theme.palette.border1}`, alignItems: 'center', padding: '0px 12px', whiteSpace: 'nowrap' }} >
                    {icon && <FontAwesomeIcon icon={icon} />}
                    <Typography variant='h6' sx={{ paddingLeft: '6px', fontSize: '18px', fontWeight: 700 }}>
                        {title}
                    </Typography>
                </Box>
                {(tabs || []).map(tab => (
                    <Box key={tab.title} sx={{ flex: 0, display: 'flex', height: '64px', background: theme.palette.panel1, borderRight: `1px solid ${theme.palette.border1}`, borderBottom: tab.selected ? `2px solid ${blue[500]}` : '', alignItems: 'center', justifyContent: 'center', padding: '0px 12px', minWidth: '130px' }} >
                        <Button disabled={tab.selected} onClick={tab.action} sx={{ textTransform: 'none' }}>
                            {tab.icon && <FontAwesomeIcon icon={tab.icon} />}
                            <Typography variant='body1' sx={{ paddingLeft: tab.icon ? '6px' : '', fontSize: '13px' }}>
                                {tab.title}
                            </Typography>
                        </Button>
                    </Box>
                ))}
                <Box sx={{ flex: 1, ...(subtitleProps || {}) }}>
                    {subtitle ? (
                        <Typography variant='body2' sx={{ fontSize: '11px' }}>
                            {subtitle}
                        </Typography>
                    ) : ''}
                </Box>
                {(buttons || []).map(button => <HeaderButton key={button.title} {...button} />)}
            </Box>
            {fixed && (
                <Box sx={{ paddingTop: '64px' }} />
            )}
        </>
    );
}

function HeaderButton(props) {
    const { tooltip, tooltipShow, title, disabled, action, thinking, icon, iconColor, width, color, submenu } = props;
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const theme = useTheme();
    const isMonadaButton = color === 'monada-button';

    if (tooltip) {
        return (
            <Tooltip title={tooltip} open={tooltipShow} onClose={() => {}} onOpen={() => {}}>
                <Box key={title} sx={{ flex: 0, display: 'flex', height: '63px', background: theme.palette.panel1, borderLeft: `1px solid ${theme.palette.border1}`, alignItems: 'center', padding: isMonadaButton ? '0px 16px' : '0px 12px' }} >
                    <Button 
                        disabled={disabled} 
                        onClick={action} 
                        sx={{ 
                            textTransform: 'none', 
                            whiteSpace: 'nowrap', 
                            width: width || 'auto',
                            ...(isMonadaButton ? {
                                borderRadius: '9999px', // Pill shape
                                border: '1px solid rgba(0, 0, 0, 0.08)',
                                background: 'linear-gradient(180deg, #fafafa 0%, #f5f5f5 100%)',
                                fontFamily: 'Poppins, sans-serif',
                                fontWeight: 500, // Medium weight
                                padding: '8px 24px', // Generous horizontal padding
                                color: '#0f172a',
                                boxShadow: 'none',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    background: 'linear-gradient(180deg, #f5f5f5 0%, #f0f0f0 100%)',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)',
                                    border: '1px solid rgba(0, 0, 0, 0.12)',
                                },
                                '&:disabled': {
                                    background: 'linear-gradient(180deg, #fafafa 0%, #f5f5f5 100%)',
                                    opacity: 0.5,
                                }
                            } : {})
                        }} 
                        color={color}
                    >
                        {thinking ?
                            <CircularProgress size={13} /> :
                            (
                                <>
                                    {icon && (
                                        <FontAwesomeIcon 
                                            icon={icon} 
                                            style={{
                                                ...(iconColor ? { color: iconColor } : {}),
                                                ...(isMonadaButton ? { 
                                                    fontWeight: 300, // Light weight for icon
                                                    fontSize: '14px'
                                                } : {})
                                            }} 
                                        />
                                    )}
                                    <Typography 
                                        variant='body1' 
                                        sx={{ 
                                            paddingLeft: icon ? '6px' : '', 
                                            fontSize: '13px',
                                            ...(isMonadaButton ? {
                                                fontFamily: 'Poppins, sans-serif',
                                                fontWeight: 500,
                                            } : {})
                                        }}
                                    >
                                        {title}
                                    </Typography>
                                </>
                            )
                        }
                    </Button>
                </Box>
            </Tooltip>
        );
    } else {
        const handleClick = (event) => {
            if (submenu) {
                setAnchorEl(event.currentTarget);
            } else if (action) {
                action(event);
            }
        };

        const handleClose = () => {
            setAnchorEl(null);
        };

        return (
            <Box key={title} sx={{ flex: 0, display: 'flex', height: '63px', background: theme.palette.panel1, borderLeft: `1px solid ${theme.palette.border1}`, alignItems: 'center', padding: isMonadaButton ? '0px 16px' : '0px 12px' }} >
                <Button 
                    disabled={disabled} 
                    onClick={handleClick}
                    sx={{ 
                        textTransform: 'none', 
                        whiteSpace: 'nowrap', 
                        width: width || 'auto',
                        ...(isMonadaButton ? {
                            borderRadius: '9999px', // Pill shape
                            border: '1px solid rgba(0, 0, 0, 0.08)',
                            background: 'linear-gradient(180deg, #fafafa 0%, #f5f5f5 100%)',
                            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Segoe UI", Roboto, sans-serif',
                            fontWeight: 500, // Medium weight
                            padding: '8px 24px', // Generous horizontal padding
                            color: '#0f172a',
                            boxShadow: 'none',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                background: 'linear-gradient(180deg, #f5f5f5 0%, #f0f0f0 100%)',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)',
                                border: '1px solid rgba(0, 0, 0, 0.12)',
                            },
                            '&:disabled': {
                                background: 'linear-gradient(180deg, #fafafa 0%, #f5f5f5 100%)',
                                opacity: 0.5,
                            }
                        } : {})
                    }} 
                    color={color}
                >
                    {thinking ?
                        <CircularProgress size={13} /> :
                        (
                            <>
                                {icon && (
                                    <FontAwesomeIcon 
                                        icon={icon} 
                                        style={{
                                            ...(iconColor ? { color: iconColor } : {}),
                                            ...(isMonadaButton ? { 
                                                fontWeight: 300, // Light weight for icon
                                                fontSize: '14px'
                                            } : {})
                                        }} 
                                    />
                                )}
                                <Typography 
                                    variant='body1' 
                                    sx={{ 
                                        paddingLeft: icon ? '6px' : '', 
                                        fontSize: '13px',
                                        ...(isMonadaButton ? {
                                            fontFamily: 'Poppins, sans-serif',
                                            fontWeight: 500,
                                        } : {})
                                    }}
                                >
                                    {title}
                                </Typography>
                            </>
                        )
                    }
                </Button>
                {submenu && (
                    <Menu
                        anchorEl={anchorEl}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        open={open}
                        onClose={handleClose}
                        PaperProps={{
                            sx: {
                                backgroundColor: '#ffffff',
                                border: '1px solid #f1f5f9', // border-slate-100
                                borderRadius: '16px', // rounded-2xl
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1), 0 0 20px rgba(0, 0, 0, 0.05)', // Layered: sharp 2px + soft 20px blur at 5% opacity
                                marginTop: '8px',
                                minWidth: '240px',
                                overflow: 'hidden'
                            }
                        }}
                        MenuListProps={{
                            sx: {
                                padding: '4px'
                            }
                        }}
                    >
                        {submenu.map((item) => item.title && item.title.startsWith && item.title.startsWith('divider') ? (
                            <Divider key={item.title} sx={{ my: 0.5 }} />
                        ) : (
                            <Tooltip key={item.title} title={item.tooltip || ''}>
                            <span style={{ display: 'block' }}>
                            <MenuItem
                                disabled={item.disabled}
                                onClick={() => {
                                    handleClose();
                                    item.action();
                                }}
                                sx={{
                                    paddingY: '14px', // py-3.5
                                    paddingX: '20px', // px-5
                                    borderRadius: '8px',
                                    margin: '2px 4px',
                                    fontFamily: 'Poppins, sans-serif',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: '#0f172a',
                                    transition: 'all 0.15s ease-in-out',
                                    '&:hover': {
                                        backgroundColor: 'rgba(99, 102, 241, 0.08)', // bg-indigo-50/40
                                        borderRadius: '8px',
                                        '& .MuiTypography-root': {
                                            color: '#0f172a'
                                        },
                                        '& .menu-item-icon': {
                                            color: '#3b82f6' // Primary brand color on hover
                                        }
                                    },
                                    '&.Mui-disabled': {
                                        opacity: 0.5
                                    }
                                }}
                            >
                                {item.icon && (
                                    <FontAwesomeIcon 
                                        icon={item.icon} 
                                        className="menu-item-icon"
                                        style={{
                                            ...(item.iconColor ? { color: item.iconColor } : {}),
                                            fontWeight: 300, // Light weight for thin appearance
                                            fontSize: '15px',
                                            color: item.iconColor || '#94a3b8', // Muted slate (#94a3b8) default
                                            marginRight: '12px',
                                            transition: 'color 0.15s ease-in-out'
                                        }} 
                                    />
                                )}
                                <Typography 
                                    variant='body1' 
                                    sx={{ 
                                        paddingLeft: item.icon ? '0px' : '', 
                                        fontSize: '14px',
                                        fontFamily: 'Poppins, sans-serif',
                                        fontWeight: 500,
                                        color: item.color || 'inherit'
                                    }}
                                >
                                    {item.title}
                                </Typography>
                            </MenuItem>
                            </span>
                            </Tooltip>
                        ))}
                    </Menu>
                )}
            </Box>
        )
    }
}